import React, { useEffect, useRef, useState } from "react";
import { Users, Plus, Trash2, BadgeCheck, Loader2, Mail, Upload, FileDown } from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TradeCombobox } from "@/components/TradeCombobox";
import { toast } from "sonner";

const empty = { company_name: "", contact_name: "", email: "", trade: "" };

export default function MyVendors() {
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState(empty);
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/buyer/approved-vendors").then((r) => setVendors(r.data.vendors || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/taxonomy").then((r) => setTrades(r.data.trades || [])).catch(() => {}); }, []);

  const downloadTemplate = () => {
    const token = localStorage.getItem("matiqs_token");
    fetch(`${API}/buyer/approved-vendors/template`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "matiqs-vendors-template.xlsx";
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Could not download the template"));
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/buyer/approved-vendors/import", fd);
      const parts = [];
      if (data.added) parts.push(`${data.added} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped?.length) parts.push(`${data.skipped.length} skipped`);
      toast.success(parts.join(" · ") || "Import complete");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not import that file");
    } finally {
      setImporting(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.email.trim()) return toast.error("Company name and email are required");
    setBusy(true);
    try {
      await api.post("/buyer/approved-vendors", form);
      toast.success(`${form.company_name} added to your vendor list`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not add that vendor");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.company_name} from your vendor list?`)) return;
    try {
      await api.delete(`/buyer/approved-vendors/${v.id}`);
      setVendors((xs) => xs.filter((x) => x.id !== v.id));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Approved vendors</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2 flex items-center gap-2">
          <Users className="w-7 h-7 text-brand" /> My Vendors
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Keep your trusted suppliers in one place. When you invite vendors to an RFQ, we suggest the ones
          whose trade matches — pick up to 10. Vendors already on Matiqs get the RFQ in their inbox; the
          rest receive it by email.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onImport} className="hidden" data-testid="vendor-import-input" />
          <Button type="button" variant="outline" data-testid="vendor-import-button" disabled={importing}
                  onClick={() => fileRef.current?.click()} className="h-10 gap-2 font-semibold">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import from Excel/CSV
          </Button>
          <button type="button" data-testid="vendor-template-link" onClick={downloadTemplate}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
            <FileDown className="w-4 h-4" /> Download sample template
          </button>
        </div>
      </div>

      <form onSubmit={add} data-testid="add-vendor-form" className="bg-card border border-border rounded-2xl p-5 grid md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-1">
          <Label>Company name</Label>
          <Input data-testid="vendor-company-input" value={form.company_name}
                 onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Gulf Steel Co" className="mt-1.5 h-10" />
        </div>
        <div className="md:col-span-1">
          <Label>Contact name</Label>
          <Input data-testid="vendor-contact-input" value={form.contact_name}
                 onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Sara" className="mt-1.5 h-10" />
        </div>
        <div className="md:col-span-1">
          <Label>Email</Label>
          <Input data-testid="vendor-email-input" type="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sales@vendor.com" className="mt-1.5 h-10" />
        </div>
        <div className="md:col-span-1">
          <Label>Trade</Label>
          <div className="mt-1.5">
            <TradeCombobox testid="vendor-trade-input" options={trades}
                           value={form.trade} onChange={(v) => setForm({ ...form, trade: v })} />
          </div>
        </div>
        <Button type="submit" data-testid="add-vendor-button" disabled={busy} className="h-10 gap-2 font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add vendor
        </Button>
      </form>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : vendors.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Users className="w-10 h-10 text-muted-foreground/40 mx-auto" />
            <p className="text-sm text-muted-foreground mt-3">No vendors yet. Add your first trusted supplier above.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="vendors-table">
            <thead className="bg-secondary text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left font-bold px-4 py-3">Company</th>
                <th className="text-left font-bold px-4 py-3">Contact</th>
                <th className="text-left font-bold px-4 py-3">Email</th>
                <th className="text-left font-bold px-4 py-3">Trade</th>
                <th className="text-right font-bold px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {vendors.map((v, i) => (
                <tr key={v.id} data-testid={`vendor-row-${i}`} className="hover:bg-secondary/40">
                  <td className="px-4 py-3 font-semibold">
                    <span className="inline-flex items-center gap-1.5">
                      {v.company_name}
                      {v.on_matiqs && (
                        <span data-testid={`vendor-onmatiqs-${i}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-brand bg-brand-soft px-1.5 py-0.5 rounded-md">
                          <BadgeCheck className="w-3 h-3" /> On Matiqs
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{v.contact_name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{v.email}</span>
                  </td>
                  <td className="px-4 py-3">{v.trade || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`vendor-delete-${i}`} onClick={() => remove(v)}
                            className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
